import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { billingApi, formatInrFromPaise } from '../api/billing.api';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import UserMenu from '../components/UserMenu';
import usePageMeta from '../hooks/usePageMeta';
import useInvoiceDownload from '../hooks/useInvoiceDownload';

const InvoiceDetailPage: React.FC = () => {
  usePageMeta({ title: 'Invoice — OnTheWeb', index: false });

  const { invoiceId = '' } = useParams();
  const navigate = useNavigate();
  const { download, downloadingId } = useInvoiceDownload();
  const { data: invoice, isLoading, isError } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => billingApi.getInvoice(invoiceId),
    enabled: Boolean(invoiceId),
  });

  const issuer = invoice?.issuer || {};
  const billing = invoice?.billing;

  return (
    <div className="nova-page min-h-screen">
      <nav className="nova-nav border-b border-nova-border">
        <div className="nova-nav-inner">
          <Logo />
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/billing')} className="btn-ghost">Billing</button>
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </nav>
      <div className="max-w-3xl mx-auto px-4 py-10 pt-24">
        {isLoading ? (
          <p className="text-nova-text2">Loading invoice...</p>
        ) : isError || !invoice || !billing ? (
          <p className="text-nova-text2">Invoice not found.</p>
        ) : (
          <div className="nova-card p-8">
            <div className="flex justify-between gap-4 mb-8">
              <div>
                <p className="section-tag mb-1">Tax invoice</p>
                <h1 className="font-display text-2xl font-bold text-nova-text">{invoice.invoiceNumber}</h1>
                <p className="text-sm text-nova-text3 mt-1">Paid {new Date(invoice.paidAt).toLocaleDateString('en-IN')}</p>
              </div>
              <button
                type="button"
                className="btn-primary h-fit"
                disabled={downloadingId === invoice._id}
                onClick={() => download(invoice._id, invoice.invoiceNumber)}
              >
                {downloadingId === invoice._id ? 'Preparing...' : 'Download PDF'}
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-8 text-sm mb-8">
              <div>
                <p className="font-semibold text-nova-text mb-1">From</p>
                <p className="text-nova-text">{issuer.legalName}</p>
                <p className="text-nova-text3">{issuer.registeredOffice}</p>
                <p className="text-nova-text3">CIN {issuer.cin}</p>
                {issuer.gstin && <p className="text-nova-text3">GSTIN {issuer.gstin}</p>}
              </div>
              <div>
                <p className="font-semibold text-nova-text mb-1">Bill to</p>
                <p className="text-nova-text">{billing.legalName}</p>
                <p className="text-nova-text3">
                  {[billing.addressLine1, billing.addressLine2, billing.city, billing.state, billing.pincode]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                <p className="text-nova-text3">{billing.email} · {billing.phone}</p>
                {billing.gstin && <p className="text-nova-text3">GSTIN {billing.gstin}</p>}
              </div>
            </div>
            <div className="border-t border-nova-border pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-nova-text2">
                <span>OnTheWeb {invoice.plan} plan{invoice.websiteName ? ` — ${invoice.websiteName}` : ''}</span>
                <span>{formatInrFromPaise(invoice.subtotalPaise)}</span>
              </div>
              <div className="flex justify-between text-nova-text2">
                <span>GST 18%</span>
                <span>{formatInrFromPaise(invoice.gstPaise)}</span>
              </div>
              <div className="flex justify-between font-semibold text-nova-text text-base pt-2">
                <span>Total</span>
                <span>{formatInrFromPaise(invoice.totalPaise)}</span>
              </div>
            </div>
            <p className="text-xs text-nova-text3 mt-8">
              <Link to="/billing" className="text-nova-sky">Back to billing</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetailPage;
